if (!customElements.get('product-selector')) {
class ProductSelector extends HTMLElement {
  constructor() {
    super();
    this.inputWrapper = '[data-input-wrapper]';
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this._selectionVersion = 0;
    this._renderController = null;
    this._syncingVariantInputs = false;
    this._outsideClick = null;
    this._selectionKey = '';
    this._variantCount = Number(this.dataset.variantCount) || this.variants.length;
    this._allowBackInStock = this.dataset.backInStock === 'true';
    const selectedData = this.querySelector('script[data-selected-variant]');
    if (selectedData) {
      try { this.cacheVariant(JSON.parse(selectedData.textContent)); } catch (_) { /* Keep the existing list. */ }
    }

    /* Authoritative Shopify variant -> assigned media mapping.
       This comes directly from Liquid, so clicking Black can always resolve
       the image actually assigned to that Black variant in Shopify admin. */
    this._variantMediaMap = new Map();
    const mediaMapData = this.querySelector('script[data-variant-media-map]');
    if (mediaMapData) {
      try {
        const entries = JSON.parse(mediaMapData.textContent);
        if (Array.isArray(entries)) {
          entries.forEach(entry => {
            if (entry && entry.id != null) this._variantMediaMap.set(String(entry.id), entry);
          });
        }
      } catch (_) {
        /* Keep legacy featured_media/featured_image fallback below. */
      }
    }

    const isModalContext = this.dataset.context === 'modal';

    if (isModalContext) {
      const modal = this.closest('quick-cart-modal') || this.closest('.quick-cart-product-modal');
      this.form = modal?.querySelector('form.product__form');
    } else {
      this.form = document.querySelector(
        `#ProductForm-${this.dataset.sectionId}`
      );
    }

    if (!isModalContext) {
      this.purchaseOptions = document.querySelector(
        `#Product-Purchase-Options-${this.dataset.sectionId}`
      );
      this.productBar = document.querySelector(
        `#ProductBar-${this.dataset.sectionId}`
      );

      if (this.dataset.installmentsFormId && this.dataset.installmentsFormId !== '#') {
        this.installmentsForm = document.querySelector(
          this.dataset.installmentsFormId
        );
      }
    } else {
      this.purchaseOptions = null;
      this.productBar = null;
      this.installmentsForm = null;
    }

    this.addEventListener('change', this.onVariantChange.bind(this));
    this.addEventListener('click', this.onSwatchClick.bind(this));

    this.updateOptions();
    this.updateVariant();
    this.updateVariantStatuses();
    this._selectionKey = this.selectionKey();
    this.initUpdateMedia();

    if (this.currentVariant && !isModalContext) {
      if (this.form && this.form.toggleAddButton) {
        this.form.toggleAddButton(!this.currentVariant.available, '');
      }
    }

    if (isModalContext && this.form) {
      const submitButton = this.form.querySelector('button[type="submit"]');
      if (submitButton && this.currentVariant) {
        submitButton.disabled = !this.currentVariant.available;
      }
    }
    this.createDropdownFromButtons();

    this.enableDropdownStyle();
  }

  connectedCallback() {
    if (this._outsideClick) window.addEventListener('click', this._outsideClick);
  }

  disconnectedCallback() {
    this._selectionVersion += 1;
    this._renderController?.abort();
    if (this._outsideClick) window.removeEventListener('click', this._outsideClick);
  }

  getProductRoot() {
    if (this.dataset.context === 'modal') {
      return this.closest('quick-cart-modal, .quick-cart-product-modal') || this.parentElement;
    }
    return document.getElementById(`MainProduct-${this.dataset.sectionId}`) ||
      this.closest('[data-main-product], .js-product') || this.parentElement;
  }

  getOptionGroups() {
    return Array.from(this.querySelectorAll(this.inputWrapper))
      .filter(wrapper => wrapper.closest('product-selector') === this);
  }

  getOptionRadios(wrapper) {
    return Array.from(wrapper.querySelectorAll('input[type="radio"]'))
      .filter(input => !input.closest('[data-cloned]'));
  }

  getSelectedControl(wrapper) {
    const radios = this.getOptionRadios(wrapper);
    return radios.length ? radios.find(input => input.checked) : wrapper.querySelector('select');
  }

  selectedOptionIds() {
    const groups = this.getOptionGroups();
    const ids = groups.map(wrapper => {
      const control = this.getSelectedControl(wrapper);
      const choice = control?.tagName === 'SELECT' ? control.selectedOptions[0] : control;
      return [choice?.dataset.optionValueId,
        choice?.closest('[data-option-value-id]')?.dataset.optionValueId,
        choice?.dataset.optionValue].find(id => /^\d+$/.test(id || '')) || '';
    });
    // Never request a partial/default selection if an ID is missing.
    return ids.length && ids.every(id => /^\d+$/.test(id)) ? ids : [];
  }

  selectionKey() {
    return JSON.stringify((this.options || []).map(option => [option.name, option.value]));
  }

  cacheVariant(variant) {
    if (!variant || !variant.id) return;
    const index = this.variants.findIndex(item => String(item.id) === String(variant.id));
    if (index < 0) this.variants.push(variant);
    else this.variants[index] = variant;
  }

  onSwatchClick(event) {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const item = target?.closest('.product-options__item--swatch, .product-options__item--image');
    if (!item || item.closest('product-selector') !== this || item.closest('[data-cloned]')) return;
    // Product-group links must keep navigating; they are not variant radios.
    if (target.closest('a[href]')) return;
    const radio = item.querySelector('input[type="radio"]');
    if (!radio || target === radio) return;
    event.preventDefault(); // Avoid a second native label click.
    if (radio.matches(':disabled') || radio.checked) return;
    radio.click(); // Use the real control and its normal change event.
  }

  updateVariantStatuses() {
    // Only infer availability when the complete variant list is present.
    // Larger catalogs receive authoritative availability from the section response.
    if (this._variantCount > this.variants.length || (!this.dataset.variantCount && this.variants.length >= 250)) return;
    this.getOptionGroups().forEach((wrapper, index) => {
      const availableValues = new Set(this.variants.filter(variant => variant.available &&
        (this.options || []).slice(0, index).every((option, previous) =>
          option.value == null || (variant.options?.[previous] ?? variant[`option${previous + 1}`]) === option.value
        )).map(variant => variant.options?.[index] ?? variant[`option${index + 1}`]));
      const controls = [...this.getOptionRadios(wrapper), ...wrapper.querySelectorAll('select option')];
      controls.forEach(input => {
        if (input.closest('[data-cloned]')) return;
        const available = availableValues.has(input.value);
        input.disabled = !available && !this._allowBackInStock && !input.getAttribute('data-stock');
        input.closest('.product-options__item')?.classList.toggle('product-options__item--disabled', !available);
        const dropdown = input.closest('dropdown-input');
        dropdown?.querySelectorAll('button[data-value]').forEach(button => {
          if (button.dataset.value === input.value) button.disabled = input.disabled;
        });
      });
    });
  }

  syncOptionAvailability(source) {
    const sourceGroups = Array.from(source.querySelectorAll(this.inputWrapper));
    this.getOptionGroups().forEach((wrapper, index) => {
      const sourceGroup = sourceGroups[index];
      if (!sourceGroup) return;
      const sourceInputs = Array.from(sourceGroup.querySelectorAll('input[type="radio"], select option'));
      const controls = [...this.getOptionRadios(wrapper), ...wrapper.querySelectorAll('select option')];
      controls.forEach(input => {
        const fresh = sourceInputs.find(candidate => candidate.value === input.value);
        if (!fresh) return;
        input.disabled = fresh.disabled;
        const item = input.closest('.product-options__item');
        const sourceItem = fresh.closest('.product-options__item');
        item?.classList.toggle('product-options__item--disabled',
          sourceItem ? sourceItem.classList.contains('product-options__item--disabled') : fresh.disabled);
        const dropdown = input.closest('dropdown-input');
        dropdown?.querySelectorAll('button[data-value]').forEach(button => {
          if (button.dataset.value === input.value) button.disabled = input.disabled;
        });
      });
    });
  }

  onVariantChange(event) {
    const target = event?.target;
    if (this._syncingVariantInputs || !(target instanceof Element) ||
      !target.matches('input[type="radio"], select') ||
      !target.closest(this.inputWrapper) || target.closest('product-selector') !== this ||
      target.closest('[data-cloned]')) return;

    this.updateOptions();
    const key = this.selectionKey();
    if (key === this._selectionKey) return;
    this._selectionKey = key;
    this._selectionVersion += 1;
    this._renderController?.abort();
    this.updateVariant();
    this.updateVariantStatuses();
    // Write the real cart/checkout variant ID before starting any request.
    this.updateVariantInput();
    this.syncPurchaseButtons();
    if (this.form?.handleErrorMessage) this.form.handleErrorMessage();

    if (this.dataset.context !== 'modal') {
      this.productBarUpdateOptions();
      this.updatePickupAvailability();
      this.updateURL();
      if (!this.currentVariant) this.setUnavailable();
      this.renderProductInfo();
    }
    this.updateMedia();
  }

  syncPurchaseButtons() {
    const variant = this.currentVariant;
    const disabled = !variant || !variant.available;
    const strings = window.theme?.strings || {};
    const unavailable = strings.unavailable || 'Unavailable';
    const soldOut = strings.soldOut || 'Sold out';
    if (this.form?.toggleAddButton) {
      this.form.toggleAddButton(disabled, !variant ? unavailable : !variant.available ? soldOut : '');
    } else {
      const submit = this.form?.querySelector('button[name="add"], button[type="submit"]');
      if (submit) {
        submit.disabled = disabled;
        const text = !variant ? unavailable : !variant.available ? soldOut :
          (this.form.closest('[data-has-pre-order]') ? strings.preorderText : strings.addToCart) || 'Add to cart';
        (submit.querySelector('span') || submit).textContent = text;
      }
    }
    // The existing custom Buy Now reads the installments variant ID.
    // Keep its disabled/reset state synchronized too; do not change its styling.
    this.getProductRoot()?.querySelectorAll('[data-dentelle-buy-now]').forEach(button => {
      button.dataset.defaultDisabled = disabled ? 'true' : 'false';
      button.disabled = disabled || button.dataset.checkoutPending === 'true';
    });
  }

  updateOptions() {
    this.options = this.getOptionGroups().map(wrapper => {
      const radios = this.getOptionRadios(wrapper);
      radios.forEach(input => {
        input.closest('.product-options__item')?.classList.toggle('is-active', input.checked);
      });
      const selected = this.getSelectedControl(wrapper);
      const reference = selected || radios[0] || wrapper.querySelector('select');
      // A select used as a mirror of the radios must not count as another option.
      if (selected && radios.length) {
        wrapper.querySelectorAll('select').forEach(select => { select.value = selected.value; });
      }
      return {
        name: reference?.dataset.name || reference?.name || wrapper.dataset.optionName || '',
        value: selected ? selected.value : null
      };
    });
  }

  productBarUpdateOptions() {
    if (this.productBar) {
      Array.from(this.productBar.querySelectorAll('select')).map(
        selector => {
          const option = this.options.find(option => option.name === (selector.dataset.name || selector.name));
          if (option && option.value != null) selector.value = option.value;
        }
      );
      this.productBar.querySelectorAll('dropdown-input').forEach(
        dropdown => {
          if (typeof dropdown.update === 'function') dropdown.update();
        }
      );
    }
  }

  updateInventoryNotice(html) {
    const root = this.getProductRoot();
    const sourceRoot = html.getElementById(`MainProduct-${this.dataset.sectionId}`) || html;
    const destination = root?.querySelector('[data-inventory-notice]');
    const source = sourceRoot.querySelector('[data-inventory-notice]');
    if (source && destination) destination.innerHTML = source.innerHTML;

    // Use the rendered quantity rule, never digits guessed from a notice/date.
    const freshInput = sourceRoot.querySelector('.product-selector__quantity quantity-input input');
    const quantityInput = root?.querySelector('.product-selector__quantity quantity-input input');
    if (!freshInput || !quantityInput) return;
    ['min', 'max', 'step'].forEach(attribute => {
      if (freshInput.hasAttribute(attribute)) quantityInput.setAttribute(attribute, freshInput.getAttribute(attribute));
      else quantityInput.removeAttribute(attribute);
    });
    const minimum = Number(quantityInput.getAttribute('min'));
    const maxAttribute = quantityInput.getAttribute('max');
    const maximum = maxAttribute == null || maxAttribute === '' ? Infinity : Number(maxAttribute);
    const value = Number(quantityInput.value);
    if (Number.isFinite(maximum) && value > maximum) quantityInput.value = maximum;
    else if (Number.isFinite(minimum) && value < minimum) quantityInput.value = minimum;
  }

  updateQuantityInput(inventoryQuantity) {
    const selectedVariantData = this.getVariantData();
    const quantityInput = this.getProductRoot()?.querySelector('.product-selector__quantity quantity-input input');

    let productUsesInventory = true;

    // check inventory is active
    if (selectedVariantData && selectedVariantData.inventory_management === null) {
      productUsesInventory = false;
    }

    // check sell on sold out is active
    if (selectedVariantData && selectedVariantData.inventory_quantity !== null && inventoryQuantity === 0 && selectedVariantData.available === true) {
      productUsesInventory = false;
    }

    if (quantityInput) {
      if (productUsesInventory) {
        quantityInput.setAttribute('max', inventoryQuantity);
        // set value accordingly to inventory
        if (quantityInput.value > inventoryQuantity) {
          quantityInput.value = inventoryQuantity;
        }

        else if (Number(quantityInput.value) == 0 && inventoryQuantity > 0) {
          quantityInput.value = 1;
        }

      } else {
        quantityInput.setAttribute('max', '');
      }
    }
  }

  updatePickupAvailability() {
    const node = this.getProductRoot()?.querySelector('pickup-availability');
    if (!node) return;
    if (this.currentVariant?.available) {
      node.dataset.variantId = this.currentVariant.id;
      node.setAttribute('available', '');
      if (typeof node.fetchAvailability === 'function') node.fetchAvailability(node.dataset.variantId);
    } else {
      node.removeAttribute('available');
      node.innerHTML = '';
    }
  }

  updateVariant() {
    this.currentVariant = this.getVariantData();
  }

  getVariantData() {
    if (!this.options?.length || this.options.some(option => option.value == null)) return undefined;
    return this.variants.find(variant => {
      const values = variant.options || [variant.option1, variant.option2, variant.option3].filter(value => value != null);
      return values.length === this.options.length &&
        this.options.every((option, index) => values[index] === option.value);
    });
  }

  initUpdateMedia() {
    // The rendered checked controls are authoritative. A stale URL must not
    // override their size/color combination when the selector initializes.
    this.updateMedia();
  }

  getSelectedMediaColor() {
    const groups = this.getOptionGroups();
    const normalizeName = value => String(value || '').replace(/^options\[(.*)\]$/i, '$1')
      .normalize('NFKC').trim().toLowerCase();
    const names = ['color', 'colour', 'colors', 'colours', 'color family', 'colour family', 'צבע', 'couleur', 'farbe', 'colore', 'اللون'];
    const named = groups.filter(group => {
      const control = this.getSelectedControl(group) || this.getOptionRadios(group)[0];
      return names.includes(normalizeName(group.dataset.optionName || control?.dataset.name || control?.name));
    });
    const swatches = groups.filter(group => group.querySelector('.product-options__item--swatch'));
    // A single swatch group also supports stores with a custom color-option name.
    const group = named.length === 1 ? named[0] : named.length === 0 && swatches.length === 1 ? swatches[0] : null;
    return group ? this.getSelectedControl(group)?.value ?? null : null;
  }

  updateMedia() {
    const root = this.getProductRoot();
    const productMedia = root?.querySelector('product-media');
    const variant = this.currentVariant;
    const mappedMedia = variant?.id != null
      ? this._variantMediaMap?.get(String(variant.id))
      : null;

    /* Prefer the Liquid-built mapping. This guarantees that the image assigned
       to the selected Shopify variant is available even when `variant | json`
       omits featured_media in this theme/context. */
    const mediaId = mappedMedia?.mediaId ?? variant?.featured_media?.id ?? null;
    const imageId = mappedMedia?.imageId ?? variant?.featured_image?.id ?? null;

    if (productMedia && typeof productMedia.setVariantMedia === 'function') {
      // Even an unavailable size/color pair may have a valid COLOR image group.
      // This does not change the selected size, availability, or cart variant.
      productMedia.setVariantMedia({ color: this.getSelectedMediaColor(), mediaId, imageId });
    } else if (productMedia && !customElements.get('product-media')) {
      if (!this._waitingForMedia) {
        this._waitingForMedia = true;
        customElements.whenDefined('product-media').then(() => {
          this._waitingForMedia = false;
          if (this.isConnected) this.updateMedia();
        });
      }
    } else if (mediaId != null && typeof productMedia?.setActiveMedia === 'function') {
      productMedia.setActiveMedia(mediaId); // Compatibility while files are being saved.
    } else if (mediaId != null && this.dataset.context === 'modal') {
      const modal = this.closest('quick-cart-modal');
      if (typeof modal?.setActiveMedia === 'function') modal.setActiveMedia(mediaId);
    }
  }

  updateURL(sellingPlanId) {
    if (this.dataset.context === 'modal') return;
    const url = new URL(this.dataset.url || window.location.pathname, window.location.origin);
    url.search = window.location.search;
    url.searchParams.delete('option_values');
    url.searchParams.delete('section_id');
    if (this.currentVariant) url.searchParams.set('variant', this.currentVariant.id);
    else {
      url.searchParams.delete('variant');
      const ids = this.selectedOptionIds();
      if (ids.length) url.searchParams.set('option_values', ids.join(','));
    }
    // An option click must not silently discard an existing selling plan.
    if (sellingPlanId) url.searchParams.set('selling_plan', sellingPlanId);
    else if (sellingPlanId === null) url.searchParams.delete('selling_plan');
    url.hash = window.location.hash;
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }

  updateVariantInput() {
    const pickerInput = this.querySelector('input[name="id"]');
    const actualForm = pickerInput?.form ||
      (this.form?.tagName === 'FORM' ? this.form : this.form?.querySelector('form'));
    const inputs = new Set([
      pickerInput,
      this.installmentsForm?.querySelector('input[name="id"]'),
      ...Array.from(this.form?.querySelectorAll('input[name="id"]') || []),
      ...Array.from(actualForm?.elements || []).filter(input => input.name === 'id')
    ]);
    this._syncingVariantInputs = true;
    try {
      inputs.forEach(input => {
        if (!input) return;
        const value = this.currentVariant ? String(this.currentVariant.id) : '';
        const disabled = !this.currentVariant;
        const changed = input.value !== value || input.disabled !== disabled;
        input.value = value;
        input.disabled = disabled;
        if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } finally {
      this._syncingVariantInputs = false;
    }
  }

  setUnavailable() {
    const price = this.getProductRoot()?.querySelector(`[id="Product-Price-${this.dataset.sectionId}"]`);
    if (price) price.classList.add('visibility-hidden');
  }

  createDropdownFromButtons() {
    const inputWrappers = this.querySelectorAll(this.inputWrapper);

    inputWrappers.forEach((wrapper) => {
      const radios = this.getOptionRadios(wrapper);
      const selectWrapper = wrapper.querySelector('[data-select-wrapper]');
      const select = wrapper.querySelector('[data-select]');

      if (!radios.length || !selectWrapper || !select) return;

      select.innerHTML = '';
      radios.forEach((radio) => {
        const option = document.createElement('option');
        option.value = radio.value;
        option.textContent = radio.value;
        const optionId = radio.dataset.optionValueId || radio.dataset.optionValue ||
          radio.closest('[data-option-value-id]')?.dataset.optionValueId;
        if (optionId) option.dataset.optionValueId = optionId;
        if (radio.checked) option.selected = true;
        if (radio.disabled) option.disabled = true;
        select.appendChild(option);
      });

      select.addEventListener('change', (e) => {
        e.stopPropagation();
        const selectedValue = e.target.value;

        // First uncheck all radios in this group
        radios.forEach(radio => {
          radio.checked = false;
          radio.closest('.product-options__item')?.classList.remove('is-active');
        });

        // Then check the selected one
        const targetRadio = Array.from(radios).find(r => r.value === selectedValue);
        if (targetRadio && !targetRadio.disabled) {
          targetRadio.checked = true;
          targetRadio.closest('.product-options__item')?.classList.add('is-active');

          // Update the options state on the parent element
          this.updateOptions();

          // Dispatch the change event to trigger the variant change
          targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      selectWrapper.classList.remove('hidden');
    });
  }

  async renderProductInfo() {
    const ids = this.selectedOptionIds();
    if (!ids.length && !this.currentVariant) return;
    const version = this._selectionVersion;
    const selection = this.selectionKey();
    this._renderController?.abort();
    const controller = new AbortController();
    this._renderController = controller;
    const url = new URL(this.dataset.url || window.location.pathname, window.location.origin);
    url.search = window.location.search;
    url.searchParams.delete('variant');
    url.searchParams.delete('option_values');
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', this.dataset.sectionId);
    if (ids.length) url.searchParams.set('option_values', ids.join(','));
    else url.searchParams.set('variant', this.currentVariant.id);

    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error(`Variant information request failed (${response.status}).`);
      const responseText = await response.text();
      if (controller.signal.aborted || !this.isConnected || version !== this._selectionVersion ||
        selection !== this.selectionKey()) return;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const source = Array.from(html.querySelectorAll('product-selector')).find(selector =>
        selector.id === this.id && selector.dataset.sectionId === this.dataset.sectionId);
      if (!source) throw new Error('Matching product selector was not returned.');

      const sourceGroups = Array.from(source.querySelectorAll(this.inputWrapper));
      const matchesSelection = sourceGroups.length === this.options.length && sourceGroups.every((wrapper, index) => {
        const control = wrapper.querySelector('input[type="radio"]:checked') || wrapper.querySelector('select');
        return control?.value === this.options[index].value;
      });
      if (!matchesSelection) throw new Error('Ignored a response for a different option selection.');

      // Update availability only. Never replace the picker or the clicked radio.
      this.syncOptionAvailability(source);
      const currentData = source.querySelector('script[data-selected-variant]');
      if (currentData) {
        const selectedVariant = JSON.parse(currentData.textContent);
        if (selectedVariant) {
          const values = selectedVariant.options || [];
          if (!selectedVariant.id || values.length !== this.options.length ||
            !values.every((value, index) => value === this.options[index].value)) {
            throw new Error('Ignored variant data that does not match the selected options.');
          }
          this.cacheVariant(selectedVariant);
        }
        this.currentVariant = selectedVariant || undefined;
      } else {
        // Backward compatibility while the snippet is being updated.
        const data = source.querySelector('script[type="application/json"]');
        const variants = data ? JSON.parse(data.textContent) : [];
        if (Array.isArray(variants)) variants.forEach(variant => this.cacheVariant(variant));
        this.updateVariant();
        if (!source.querySelector('input[name="id"]')?.value) this.currentVariant = undefined;
      }
      this.updateVariantInput();
      this.syncPurchaseButtons();
      this.updateURL();
      this.updatePickupAvailability();
      this.updateMedia();
      this.updateInventoryNotice(html);
      const root = this.getProductRoot();
      [`Product-Price-${this.dataset.sectionId}`, `Product-Purchase-Options-${this.dataset.sectionId}`].forEach(id => {
        const destination = root?.querySelector(`[id="${id}"]`);
        const fresh = html.getElementById(id);
        if (!destination || !fresh) return;
        destination.innerHTML = fresh.innerHTML;
        destination.classList.toggle('visibility-hidden', !this.currentVariant);
      });
    } catch (error) {
      if (error.name !== 'AbortError' && version === this._selectionVersion) {
        console.warn('Product selector:', error.message);
      }
      // Keep the most recent local selection. Never restore an earlier variant.
    }
  }

  enableDropdownStyle() {
    const wrappers = Array.from(this.querySelectorAll('.js-product-options-value-list'));
    const isSwatch = wrapper => !!wrapper.querySelector('.product-options__item--swatch, .product-options__item--image');
    const closeDropdown = wrapper => {
      wrapper.dataset.open = 'false';
      wrapper.querySelectorAll('li[data-cloned]').forEach(clone => clone.remove());
      wrapper.querySelectorAll('li').forEach(li => { li.style.display = ''; });
    };
    const selectRadio = radio => {
      if (!radio || radio.matches(':disabled')) return;
      const group = radio.closest(this.inputWrapper);
      this.getOptionRadios(group).forEach(input => { input.checked = input === radio; });
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const createClone = (li, wrapper) => {
      const clone = li.cloneNode(true);
      clone.setAttribute('data-cloned', 'true');
      clone.setAttribute('aria-hidden', 'true');
      const radio = clone.querySelector('input[type="radio"]');
      const label = clone.querySelector('label');
      if (radio) {
        radio.id += '-cloned';
        radio.removeAttribute('name');
        radio.removeAttribute('form');
        radio.tabIndex = -1;
        label?.setAttribute('for', radio.id);
      }
      clone.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        closeDropdown(wrapper);
        selectRadio(li.querySelector('input[type="radio"]'));
      });
      return clone;
    };
    const openDropdown = wrapper => {
      wrappers.forEach(other => { if (other !== wrapper) closeDropdown(other); });
      wrapper.dataset.open = 'true';
      const ul = wrapper.querySelector('ul');
      if (!ul) return;
      const selected = ul.querySelector('.product-options__item.is-active');
      if (selected?.closest('li')) ul.insertBefore(createClone(selected.closest('li'), wrapper), ul.firstElementChild);
      ul.querySelectorAll('li').forEach(li => { li.style.display = 'block'; });
    };
    wrappers.forEach(wrapper => {
      if (isSwatch(wrapper)) {
        closeDropdown(wrapper); // Swatches remain direct-select controls, never dropdown clones.
        return;
      }
      if (wrapper.closest('.product-selector--buttons')) return;
      wrapper.dataset.open = 'false';
      wrapper.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        if (!target || target.closest('li[data-cloned]') || target.closest('a[href], select, option')) return;
        const item = target.closest('.product-options__item');
        const radio = item?.querySelector('input[type="radio"]');
        // Let direct radio/keyboard activation retain native checked semantics.
        if (target === radio) { closeDropdown(wrapper); return; }
        event.preventDefault();
        event.stopPropagation();
        if (wrapper.dataset.open === 'false') openDropdown(wrapper);
        else if (radio) { closeDropdown(wrapper); selectRadio(radio); }
        else closeDropdown(wrapper);
      });
    });
    if (this._outsideClick) window.removeEventListener('click', this._outsideClick);
    this._outsideClick = event => wrappers.forEach(wrapper => {
      if (!wrapper.contains(event.target)) closeDropdown(wrapper);
    });
    window.addEventListener('click', this._outsideClick);
  }
}

customElements.define('product-selector', ProductSelector);
}
